import React, { useEffect, useState } from "react";
import { Icons } from "../icons.jsx";
import { useCreateSkillsAcademyEnrollmentRequest } from "../useSkillsAcademy.js";

// TEMPORARY: localStorage spam guard — remove once server-side enrollment tracking is in place
const STORAGE_KEY = (programId: string) => `enrolment_requested_${programId}`;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const hasRequested = (programId: string) => {
  const ts = localStorage.getItem(STORAGE_KEY(programId));
  return !!ts && Date.now() - Number(ts) < ONE_DAY_MS;
};
const markRequested = (programId: string) =>
  localStorage.setItem(STORAGE_KEY(programId), String(Date.now()));
// END TEMPORARY
import {
  EnquiryModalBackdrop,
  EnquiryModalPanel,
  EnquiryModalCloseBtn,
  EnquiryModalHeader,
  EnquiryModalProgramTag,
  EnquiryModalTitle,
  EnquiryModalSubtitle,
  EnquiryModalActions,
  EnquiryModalSubmitBtn,
  EnquiryModalSuccess,
  EnquiryModalSuccessTitle,
  EnquiryModalSuccessText,
} from "./style.js";

type Props = {
  program: {
    id: string;
    name: string;
    categories?: { name: string }[];
  };
  onClose: () => void;
};

const EnquiryModal = ({ program, onClose }: Props) => {
  const { mutate, isPending, isSuccess } = useCreateSkillsAcademyEnrollmentRequest();
  const category = program.categories?.[0]?.name ?? "";

  // TEMPORARY: initialise from localStorage so the success state persists across opens
  const [alreadyRequested, setAlreadyRequested] = useState(() =>
    hasRequested(program.id),
  );
  // END TEMPORARY

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <EnquiryModalBackdrop onClick={onClose}>
      <EnquiryModalPanel onClick={(e) => e.stopPropagation()}>
        <EnquiryModalCloseBtn onClick={onClose}>
          <Icons.x size={18} />
        </EnquiryModalCloseBtn>

        {/* TEMPORARY: show success if already requested via localStorage */}
        {isSuccess || alreadyRequested ? (
          <EnquiryModalSuccess>
            <EnquiryModalSuccessTitle>
              We'll be in touch!
            </EnquiryModalSuccessTitle>
            <EnquiryModalSuccessText>
              Thanks for your interest in {program.name}. We'll reach out with
              more information shortly.
            </EnquiryModalSuccessText>
          </EnquiryModalSuccess>
        ) : (
          <>
            <EnquiryModalHeader>
              {category && (
                <EnquiryModalProgramTag>{category}</EnquiryModalProgramTag>
              )}
              <EnquiryModalTitle>
                Enquire about this programme
              </EnquiryModalTitle>
              <EnquiryModalSubtitle>
                Interested in {program.name}? Click below and we'll get back to
                you with more information.
              </EnquiryModalSubtitle>
              <EnquiryModalActions>
                <EnquiryModalSubmitBtn
                  type="button"
                  disabled={isPending}
                  onClick={() => {
                    mutate(program.id, {
                      // TEMPORARY: persist to localStorage on success
                      onSuccess: () => {
                        markRequested(program.id);
                        setAlreadyRequested(true);
                      },
                      // END TEMPORARY
                    });
                  }}
                >
                  {isPending ? "Sending…" : "Send enquiry"}
                </EnquiryModalSubmitBtn>
              </EnquiryModalActions>
            </EnquiryModalHeader>
          </>
        )}
      </EnquiryModalPanel>
    </EnquiryModalBackdrop>
  );
};

export default EnquiryModal;
